import Head from "next/head";
import { useRef, useState, useEffect } from "react";
import { useRouter } from "next/router";
import {
  ShoppingCartIcon,
  FireIcon as CommunityIcon,
} from "@heroicons/react/solid";
import cx from "classnames";
import { AccessIcon } from "components/Icons";
import Link from "next/link";
import Image from "next/image";
import { FireIcon, ReceiptTaxIcon } from "@heroicons/react/outline";
import useLocalStorage from "hooks/useLocalStorage";
import { Expert, CommunityItem } from "types/user";
import {
  getFullName,
  handleClipboard,
  truncateString,
  isFacebookSupportedImage,
  findCountryByCode,
  trackPageView,
  trackViewContent,
  cloudfront,
  toast,
  makeUrlsClickable,
  roundTo,
} from "utils";
// import Breadcrumbs from "components/server/Breadcrumbs";
import Button from "components/Button/Button";
import OfferShareButton from "components/OfferShareButton/OfferShareButton";
import OfferAttachment from "components/OfferAttachment/OfferAttachment";
import { OfferSignIn } from "components/OfferSignIn";
import { OfferSignInUpOtp } from "components/OfferSignInUpOtp";
import { HeartIcon } from "@heroicons/react/outline";
import Script from "next/script";
import { useTranslation } from "react-i18next";
// import ChatIcon from "components/Icons/ChatIcon/ChatIcon";
import { useMutation } from "@tanstack/react-query";
import { api } from "utils/api";
import Modal from "components/Modal/Modal";
import PromotionModalActions from "components/PromotionModalActions/PromotionModalActions";
// import Cookies from "js-cookie";

const CommunityPage = ({
  expert,
  community,
  query,
}: {
  expert: Expert;
  community: CommunityItem;
  query;
}) => {
  const router = useRouter();
  const publicId = router.query.publicId as string;
  const communityId = router.query.communityId as string;
  const [hasAuthForm, setAuthForm] = useState<"sign-in" | "sign-up">(null);
  const [selectedPlan, setSelectedPlan] = useState(0);
  const chatSignUpRef = useRef(null);
  const chatSignInRef = useRef(null);
  const { t } = useTranslation();
  const [, setCommunityBuyFlow] = useLocalStorage(
    `community-${communityId}`,
    null
  );
  const [, setSelectedPlanIndex] = useLocalStorage(
    `communitySelectIndex-${communityId}`,
    null
  );
  const [isChatOpenTrue, setOpenChat] = useLocalStorage(
    `community-chat-${communityId}`,
    null
  );
  const [isFollowTrue, setFollowChat] = useLocalStorage(
    `community-follow-${communityId}`,
    null
  );
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
  const { promo } = router.query;
  const [code, setCode] = useState(promo || "");
  const [promocode, setPromo] = useState("");
  const [promotionCode, setPromotionCode] = useState<any>(undefined);

  function promoModalClose() {
    setPromo("");
    setIsPromoModalOpen(false);
  }
  const handleCheckPromotion = async () => {
    try {
      await checkPromotion({
        code: promocode,
        offerid: community?.offerId,
      });
    } catch (error) {
      setCode("");
      setPromo("");
      console.error("Error checking promotion:", error);
    }
  };

  const handleOpenPromoModal = () => {
    setIsPromoModalOpen(true);
  };

  const { mutateAsync: checkPromotion, isLoading: checkPromotionLoader } =
    useMutation(
      (payload: { code: string; offerid?: string }) =>
        api.post("/experts/v1/experts/check-promotion", payload),
      {
        onSuccess: (data) => {
          setCode(promocode);
          // Cookies.set("promo", promocode);
          // Cookies.set("slug", community?.offerSlug);
          if (
            data?.status === "PROMOTION_NOT_FOUND" ||
            data?.status === "PROMOTION_LIMIT_REACHED" ||
            data?.status === "PROMOTION_EXPIRED" ||
            data?.status === "PROMOTION_DISABLED" ||
            data?.status === "PROMOTION_DELETED"
          ) {
            setCode("");
            toast(
              {
                title: t("promoCodeIncorrectExpired"),
                icon: "error",
              },
              { type: "custom" }
            );
          } else {
            setCode(promocode);
            setPromotionCode(data);
            toast(t("promoCodeAddedToast"), { type: "success" });
            router.push(
              `/${publicId}/community/${communityId}` +
                (promocode ? `?promo=${promocode}` : "")
            );
          }
          promoModalClose();
        },
        onError: (error) => {
          setCode("");
          promoModalClose();
          toast(
            {
              title: t("promoCodeIncorrectExpired"),
              icon: "error",
            },
            { type: "custom" }
          );
        },
      }
    );

  const { mutateAsync: promotion, isLoading: promotionLoadingCode } =
    useMutation(
      (payload: { code: string | any; offerid?: string }) =>
        api.post("/experts/v1/experts/check-promotion", payload),
      {
        onSuccess: (data) => {
          if (
            data?.status === "PROMOTION_NOT_FOUND" ||
            data?.status === "PROMOTION_LIMIT_REACHED" ||
            data?.status === "PROMOTION_EXPIRED" ||
            data?.status === "PROMOTION_DISABLED" ||
            data?.status === "PROMOTION_DELETED"
          ) {
            handleLeavePage();
            setCode("");
            toast(
              {
                title: t("promoCodeIncorrectExpired"),
                icon: "error",
              },
              { type: "custom" }
            );
            router.push(`/${publicId}/community/${communityId}`);
          } else {
            setPromotionCode(data);
            router.push(
              `/${publicId}/community/${communityId}` +
                (code ? `?promo=${code}` : "")
            );
          }
        },
        onError: (error) => {
          handleLeavePage();
          setCode("");
          toast(
            {
              title: t("promoCodeIncorrectExpired"),
              icon: "error",
            },
            { type: "custom" }
          );
        },
      }
    );

  useEffect(() => {
    if (code) {
      checkPromo();
    }
  }, []);

  const handleLeavePage = () => {
    const { promo, ...query } = router.query;
    router.replace(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true }
    );
  };

  const checkPromo = async () => {
    try {
      await promotion({
        code: code,
        offerid: community?.offerId,
      });
    } catch (error) {
      console.error("Error checking promotion:", error);
    }
  };

  useEffect(() => {
    if (community?.metapixelid || expert?.metapixelid) {
      trackPageView(
        community?.metapixelid || expert?.metapixelid,
        community?.offerSlug,
        community?.name
      );
    }
  }, [expert?.metapixelid, community?.metapixelid]);

  useEffect(() => {
    if (community) {
      trackViewContent(
        community?.offerSlug,
        "community",
        community?.name,
        1,
        community?.levels[selectedPlan]?.price,
        community?.currency,
        expert?.publicId,
        community?.metapixelid || expert?.metapixelid,
        community?.gtmid || expert?.gtmid
      );
    }
  }, [
    community,
    expert?.publicId,
    expert?.gtmid,
    community?.metapixelid,
    community?.gtmid,
  ]);

  const countryCurrency = findCountryByCode(community?.currency);
  const IconComponent: any = countryCurrency?.icon;

  const handlePlanChange = (index) => {
    setSelectedPlan(index);
  };

  const handleCopy = () => {
    handleClipboard({
      text: `${publicId}/community/${communityId}`,
      toastText: "toastOfferUrlCopied",
    });
  };

  const openChat = () => {
    setOpenChat(true);
    setAuthForm("sign-up");
  };

  const handleFollow = () => {
    setFollowChat(true);
    setAuthForm("sign-up");
  };

  useEffect(() => {
    if (query.p === "") {
      if (!isChatOpenTrue || !isFollowTrue) {
        setSelectedPlanIndex(selectedPlan);
        setCommunityBuyFlow(true);
        setAuthForm("sign-up");
        if (isMobile) {
          setTimeout(() => {
            window.scrollTo({
              top: document.body.scrollHeight,
              behavior: "smooth",
            });
          }, 100);
        } else if (!isMobile) {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    }
  }, [query]);

  const isMobile =
    typeof window !== "undefined" &&
    /Mobi|Android/i.test(window.navigator.userAgent);

  const handleBuy = () => {
    if (!isChatOpenTrue || !isFollowTrue) {
      setSelectedPlanIndex(selectedPlan);
      setCommunityBuyFlow(true);
      setAuthForm("sign-up");
      if (isMobile) {
        setTimeout(() => {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: "smooth",
          });
        }, 100);
      } else if (!isMobile) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  const { firstName, lastName, avatar, freeChat, isOnline, country } = expert;

  const fullName = getFullName(firstName, lastName, publicId);

  function calculateDiscountedPrice(originalPrice, discountPercent) {
    let discountAmount = originalPrice * (discountPercent / 100);
    let discountedPrice = originalPrice - discountAmount;
    return discountedPrice;
  }

  const levelsWithDiscounts = community?.levels.map((level) => {
    const discountLevel = promotionCode?.levels_discount.find(
      (discount) => discount.levelid === level.levelid
    );

    if (discountLevel) {
      return {
        ...level,
        price:
          Math.round(
            calculateDiscountedPrice(level.price, discountLevel.discount) * 100
          ) / 100,
      };
    }

    return level;
  });
  if (!community) {
    return (
      <div className="flex items-center justify-center w-full h-screen -mt-16 pt-16 text-center">
        <div className="flex flex-col items-center max-w-sm">
          <Image
            src="/img/empty-expert.svg"
            width={128}
            height={128}
            alt="no expert"
          />
          <h3 className="font-bold mt-20 text-lg">{t("emptyExpertTitle")}</h3>
          <p className="text-sm font-medium opacity-50 mt-4">
            {t("emptyExpertText")}
          </p>
        </div>
      </div>
    );
  }

  const isFreeCommunity = levelsWithDiscounts[selectedPlan]?.price === 0;

  const totalDiscount =
    levelsWithDiscounts?.length > 0 &&
    roundTo(
      community?.levels[selectedPlan]?.price -
        levelsWithDiscounts[selectedPlan]?.price,
      2
    );

  const title = `${community?.name ?? ""} - ${fullName}`;
  const description = truncateString(community?.description, 150);

  const cover = community?.cover;
  const avatarOrGeneric = isFacebookSupportedImage(avatar)
    ? avatar
    : "https://waylight.me/wlgenericpreview.png";
  const isProperImage = isFacebookSupportedImage(cover);

  const gtmid = expert?.gtmid;
  const hotjarid = expert?.hotjarid;
  const metapixelid = expert?.metapixelid;
  const isSoldEnabled = expert?.isSoldEnabled;

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta
          property="og:image"
          content={
            cover
              ? isProperImage
                ? cloudfront + cover
                : avatarOrGeneric
              : avatarOrGeneric
          }
        />

        <meta
          property="og:url"
          content={`https://waylight.me/${publicId}/community/${community?.offerSlug}`}
        />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        <meta
          property="twitter:image"
          content={
            cover
              ? isProperImage
                ? cloudfront + cover
                : avatarOrGeneric
              : avatarOrGeneric
          }
        />
        <meta property="twitter:card" content="summary" />
        {community?.gtmid || gtmid ? (
          <script
            dangerouslySetInnerHTML={{
              __html: `
     window.dataLayer = window.dataLayer || [];
     function gtag(){dataLayer.push(arguments);}
     gtag('js', new Date());
     gtag('config', '${community?.gtmid || gtmid}');
   `,
            }}
          />
        ) : null}
      </Head>

      {community?.gtmid || gtmid ? (
        <Script
          id="profilegtm"
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                 new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                 j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                 'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                 })(window,document,'script','dataLayer','${
                   community?.gtmid || gtmid
                 }');`,
          }}
        />
      ) : null}

      {hotjarid ? (
        <Script
          id="profilehotjar"
          dangerouslySetInnerHTML={{
            __html: `(function(h,o,t,j,a,r){
                           h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                           h._hjSettings={hjid:${hotjarid},hjsv:6};
                           a=o.getElementsByTagName('head')[0];
                           r=o.createElement('script');r.async=1;
                           r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                           a.appendChild(r);
                       })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
          }}
        />
      ) : null}

      {community?.metapixelid || metapixelid ? (
        <Script
          id="profilemetanew"
          dangerouslySetInnerHTML={{
            __html: `!function(f,b,e,v,n,t,s)
                                       {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                                       n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                                       if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                                       n.queue=[];t=b.createElement(e);t.async=!0;
                                       t.src=v;s=b.getElementsByTagName(e)[0];
                                       s.parentNode.insertBefore(t,s)}(window, document,'script',
                                       'https://connect.facebook.net/en_US/fbevents.js');
                                       fbq('init', '${
                                         community?.metapixelid || metapixelid
                                       }');
                                    
                                      `,
          }}
        />
      ) : null}

      <div
        className={cx("mx-auto px-4", {
          "max-w-7xl xl:px-8": hasAuthForm || community?.levels?.length > 1,
          "max-w-3xl lg:max-w-4xl lg:px-14 pb-4":
            !hasAuthForm && community?.levels?.length === 1,
        })}
      >
        {/* <Breadcrumbs
          pages={[
            { name: t("explore"), url: "/" },
            {
              name: fullName,
              url: `/${publicId}#communities`,
            },
            { name: t("community") },
          ]}
        /> */}
        <div className="pb-6 pt-6 flex md:pb-4 flex-col md:flex-row">
          <div className="flex flex-col sm:items-start basis-full">
            <div className="flex gap-4 items-center">
              <Link href={`/${publicId}#communities`} passHref>
                <div className="relative mr-0  drop-shadow-4sm cursor-pointer">
                  <img
                    alt="Avatar"
                    src={avatar}
                    className="border-2 border-white bg-avatar-gradient w-12 h-12 sm:w-[60px] sm:h-[60px] object-cover rounded-full max-w-none"
                  />
                  <span
                    className={cx(
                      "absolute z-10 top-1 right-1 inline-flex items-center justify-center p-1 text-xs font-bold leading-none rounded-full border-2 border-white",
                      {
                        "bg-red-600": !isOnline,
                        "bg-green-600": isOnline,
                      }
                    )}
                  />
                </div>
              </Link>
              <h2 className="break-words max-w-full text-lg font-semibold leading-5 text-slate-900 sm:text-2xl">
                {community?.name}
              </h2>
            </div>
            <div className="py-3 flex flex-wrap gap-x-3 gap-y-1 font-medium text-sm text-slate-500">
              {promotionCode && (
                <span className="flex items-center first-letter:uppercase">
                  <ReceiptTaxIcon className="w-5 h-5 mr-1 text-custom-dark-blue stroke-2" />{" "}
                  <span className="font-semibold text-custom-dark-blue">
                    {promotionCode.code}
                  </span>
                </span>
              )}
              <span className="flex items-center first-letter:uppercase">
                <CommunityIcon className="w-5 h-5 mr-1" />{" "}
                <span className="first-letter:uppercase">{t("community")}</span>
              </span>
              <span className="flex items-center whitespace-nowrap">
                <IconComponent className="shrink-0 w-5 h-5 mr-1 bg-slate-500 text-white" />
                <span
                  className={
                    promotionCode &&
                    community?.levels[selectedPlan]?.price !== 0 &&
                    totalDiscount !== 0
                      ? "line-through"
                      : ""
                  }
                >
                  {community?.levels[selectedPlan]?.price}
                </span>
                {promotionCode &&
                community?.levels[selectedPlan]?.price !== 0 &&
                totalDiscount !== 0 ? (
                  <span className="ml-1">
                    {isFreeCommunity
                      ? "0.00"
                      : levelsWithDiscounts[selectedPlan]?.price}
                  </span>
                ) : null}
              </span>
              {community?.levels[selectedPlan]?.expiration > 0 ? (
                <span className="flex items-center whitespace-nowrap text-slate-500 -ml-2">
                  ({community?.levels[selectedPlan]?.expiration} {t("days")})
                </span>
              ) : (
                <span className="flex items-center whitespace-nowrap text-slate-500 -ml-2">
                  ({t("unlimited")})
                </span>
              )}
              {isSoldEnabled ? (
                <>
                  <span className="flex items-center custom-dark-blue">
                    <ShoppingCartIcon className="w-5 h-5 mr-1 text-custom-dark-blue" />
                    <span className="flex items-center custom-dark-blue">
                      {community?.sold}
                    </span>
                  </span>
                </>
              ) : null}
            </div>
          </div>
          <div className="flex basis-auto flex-row sm:flex-row sm:shrink-0 items-start gap-2">
            <Button
              onClick={handleFollow}
              type="rounded"
              className=""
              icon={<HeartIcon className="text-slate-500 h-4 w-4" />}
            />
            {/* <Button
              icon={<ChatIcon className="stroke-slate-500 h-4 w-4" />}
              type="rounded"
              onClick={openChat}
              className={
                freeChat ? "!border-slate-500 text-slate-900 mr-0" : "hidden"
              }
            /> */}
            <OfferShareButton onClick={handleCopy} />
          </div>
        </div>
        <div className="lg:grid grid-cols-12 gap-4">
          <div
            className={cx("bg-slate-50 p-4 shadow rounded-xl mb-4", {
              "col-span-7": hasAuthForm || community?.levels?.length > 1,
              "col-span-12": !hasAuthForm || community?.levels?.length === 1,
            })}
          >
            <div className="text-sm mb-4 whitespace-pre-line break-words">
              {makeUrlsClickable(community?.description)}
            </div>

            <OfferAttachment
              attachemntUrl={
                community?.cover !== null ? cloudfront + community?.cover : null
              }
              adaptivePlaylistCover={community?.adaptivePlaylistCover}
              cover={true}
            />

            {hasAuthForm ? null : community?.levels?.length === 1 ? (
              <div className="mx-auto w-[300px] mt-4">
                <div data-testid="buy-button">
                  <Button
                    icon={
                      community?.levels[selectedPlan]?.expiration > 0 ? (
                        <FireIcon className="h-6 w-6" />
                      ) : (
                        <AccessIcon className="h-5 w-5" />
                      )
                    }
                    type="danger"
                    onClick={handleBuy}
                    fluid
                  >
                    {community?.trial > 0 &&
                    community?.levels[selectedPlan]?.price !== 0
                      ? t("tryForFree")
                      : `${t(
                          community?.levels[selectedPlan]?.expiration > 0
                            ? isFreeCommunity
                              ? "subscribeForFree"
                              : "subscribeFor"
                            : isFreeCommunity &&
                              community?.levels[selectedPlan]?.expiration ===
                                "0"
                            ? "buyAccessForFree"
                            : "buyAccessFor"
                        )}${
                          !isFreeCommunity
                            ? ` ${countryCurrency?.symbol}${levelsWithDiscounts[selectedPlan]?.price}`
                            : ""
                        }`}
                  </Button>
                </div>

                {!promotionCode ? (
                  <div className="flex justify-center cursor-pointer text-custom-dark-blue mt-4 text-sm">
                    <button onClick={handleOpenPromoModal}>
                      + {t("enterPromoCodePromotions")}
                    </button>
                  </div>
                ) : null}
                {community?.trial > 0 && (
                  <span className="text-gray-900 text-sm flex items-start justify-center gap-1 mt-3">
                    {t("trialPeriod")} {community?.trial} {t("days")}
                  </span>
                )}
                <p className="flex items-start justify-center gap-1 mt-3">
                  <span className="text-gray-900 text-sm">
                    {community?.levels[selectedPlan]?.price === 0
                      ? null
                      : community?.levels[selectedPlan]?.expiration > 0
                      ? t("paymentOccurs") +
                        " " +
                        community?.levels[selectedPlan]?.expiration +
                        " " +
                        t("days") +
                        "."
                      : t("paymentOccursOneTime")}
                  </span>
                </p>
              </div>
            ) : null}
          </div>
          {hasAuthForm ? (
            <div className="mx-auto col-span-4 rounded-xl sm:rounded-lg lg:shadow md:p-4 lg:p-4 max-w-md lg:min-w-full mb-auto">
              {hasAuthForm === "sign-up" ? (
                <OfferSignInUpOtp
                  type="community"
                  handleAuthForm={setAuthForm}
                  emailRef={chatSignUpRef}
                  publicId={publicId}
                  offerId={communityId}
                  code={code}
                />
              ) : (
                <OfferSignIn
                  type="community"
                  handleAuthForm={setAuthForm}
                  emailRef={chatSignInRef}
                  publicId={publicId}
                  offerId={communityId}
                />
              )}
            </div>
          ) : null}
          {community?.levels?.length > 1 && !hasAuthForm ? (
            <div className="col-span-5">
              <div className="mx-auto rounded-xl sm:rounded-lg lg:shadow p-4 mb-auto">
                <h1 className="mb-5 text-base text-center">
                  {t("selectSubscription")}
                </h1>
                {community?.levels.map((plan, index) => {
                  const discountLevel = promotionCode?.levels_discount.find(
                    (discount) => discount.levelid === plan.levelid
                  );

                  const discount = discountLevel
                    ? discountLevel.discount
                    : null;

                  return (
                    <div
                      key={plan.name}
                      className={`m-auto w-11/12 flex items-start shadow rounded-xl mb-4 p-4 cursor-pointer ${
                        selectedPlan === index ? "border border-slate-500" : ""
                      }`}
                      onClick={() => handlePlanChange(index)}
                    >
                      <input
                        type="radio"
                        id={plan.name}
                        name="plan"
                        value={index}
                        checked={selectedPlan === index}
                        onChange={() => {}}
                        className={`mr-3 mt-1 appearance-none checked:bg-black rounded-full checked:border-transparent focus:outline-none focus:ring-0`}
                      />
                      <label htmlFor={plan.name} className="text-base">
                        <div className="text-grey-900 font-medium">
                          {plan.name}
                        </div>
                        <div>
                          <span className="text-grey-900 font-medium">
                            {countryCurrency?.symbol}
                            {plan.price}
                          </span>{" "}
                          {Number(discount) > 0 && (
                            <span className="text-slate-500">
                              {" "}
                              (-{discount}%)
                            </span>
                          )}{" "}
                          {plan.price !== 0 && (
                            <span className="text-slate-500">
                              {plan.expiration > 0
                                ? `(${plan.expiration} ${t("days")})`
                                : `(${t("unlimited")})`}
                            </span>
                          )}
                        </div>
                      </label>
                    </div>
                  );
                })}
                <div className="flex flex-col items-center justify-center w-[250px] md:w-[300px] mx-auto">
                  <div data-testid="buy-button">
                    {" "}
                    <Button
                      icon={
                        community?.levels[selectedPlan]?.expiration > 0 ? (
                          <FireIcon className="h-6 w-6" />
                        ) : (
                          <AccessIcon className="h-5 w-5" />
                        )
                      }
                      type="danger"
                      onClick={handleBuy}
                      fluid
                    >
                      {community.trial > 0 &&
                      community?.levels[selectedPlan]?.price !== 0
                        ? t("tryForFree")
                        : `${t(
                            community?.levels[selectedPlan]?.expiration > 0
                              ? isFreeCommunity
                                ? "subscribeForFree"
                                : "subscribeFor"
                              : isFreeCommunity &&
                                community?.levels[selectedPlan]?.expiration ===
                                  "0"
                              ? "buyAccessForFree"
                              : "buyAccessFor"
                          )}${
                            !isFreeCommunity
                              ? ` ${countryCurrency?.symbol}${levelsWithDiscounts[selectedPlan]?.price}`
                              : ""
                          }`}
                    </Button>{" "}
                  </div>

                  {!promotionCode ? (
                    <div className="flex justify-center cursor-pointer text-custom-dark-blue mt-4 text-sm">
                      <button onClick={handleOpenPromoModal}>
                        + {t("enterPromoCodePromotions")}
                      </button>
                    </div>
                  ) : null}
                  {community?.trial > 0 && (
                    <span className="text-gray-900 text-sm flex items-start justify-center gap-1 mt-3">
                      {t("trialPeriod")} {community?.trial} {t("days")}
                    </span>
                  )}
                  <p className="flex items-start justify-center gap-1 mt-2 mb-3">
                    <span className="text-gray-900 text-sm">
                      {community?.levels[selectedPlan]?.price === 0
                        ? null
                        : community?.levels?.[selectedPlan].expiration > 0
                        ? t("paymentOccurs") +
                          " " +
                          community?.levels?.[selectedPlan].expiration +
                          " " +
                          t("days") +
                          "."
                        : t("paymentOccursOneTime")}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          ) : null}
          <div className="mx-auto md:hidden py-4 px-4 md:px-6 h-[205px]" />
        </div>
      </div>
      <Modal
        isOpen={isPromoModalOpen}
        showCloseBtn={true}
        title={t("enterPromoCodePromotions")}
        icon="promotion"
        onClose={promoModalClose}
      >
        <div className="relative mb-6">
          <ReceiptTaxIcon className="absolute left-3 top-[12px] transform -t-y-1/2 h-5 w-5 text-slate-500" />
          <input
            className="shadow appearance-none border border-slate-300 text-gray-900 rounded w-full py-3 pl-10 pr-3 text-sm font-semibold leading-tight focus:outline-none focus:shadow-outline"
            id="promo-code"
            type="text"
            value={promo}
            onChange={(e) => setPromo(e.target.value)}
          />
        </div>
        <PromotionModalActions
          cancelLabel={t("buttonCancel")}
          confirmLabel={t("confirm")}
          loading={checkPromotionLoader}
          onCancel={promoModalClose}
          onConfirm={handleCheckPromotion}
        />
      </Modal>
    </>
  );
};

export default CommunityPage;
